import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';

// 3rd party libraries
import _ from 'lodash';
import { Fzf } from 'fzf';

// React and React Native Core imports
import Page from '../../atoms/Page';
import Card from '../../atoms/Card';
import CreateBarnForm from '../../atoms/CreateBarnForm';
import TextInput from '../../atoms/TextInput';

// Our Own imports
import { Privilege, useCan } from '@feedflo/access-control';
import { useCensor } from '../../utils/hooks/useCensor';

// Relative path imports
import './OrgBarnsTab.scss';
import { ORG_BARNS_QUERY } from './queries';

const asUserContext = {
  headers: {
    'x-hasura-role': 'user',
  },
};

const STRUCTURE_COLUMNS = [
  { field: 'name', label: 'Barn Name' },
  { field: 'feedLineCount', label: 'Feed Lines' },
  { field: 'roomCount', label: 'Rooms' },
  { field: 'binCount', label: 'Bins' },
  { field: 'deviceCount', label: 'Devices' },
];

function getBarnStructureCounts(barn) {
  const feedLineCount = barn.feed_lines_aggregate?.aggregate?.count ?? 0;
  const roomCount = barn.rooms_aggregate?.aggregate?.count ?? 0;
  const binCount = barn.bins_aggregate?.aggregate?.count ?? 0;
  const feedLineDeviceCount =
    barn.feed_lines?.reduce(
      (sum, feedLine) => sum + (feedLine.device_assignments_aggregate?.aggregate?.count ?? 0),
      0,
    ) ?? 0;
  const binControllerCount =
    barn.bin_sets?.reduce((sum, binSet) => sum + (binSet.bin_controllers_aggregate?.aggregate?.count ?? 0), 0) ?? 0;

  return {
    feedLineCount,
    roomCount,
    binCount,
    deviceCount: feedLineDeviceCount + binControllerCount,
  };
}

function OrgBarnsTab({ titleSegments = [], organizationId = null }) {
  // --- State hooks ---
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // --- Custom hooks ---
  const { can } = useCan();
  const { censor } = useCensor();

  // --- Derived state ---
  const pageTitleSegments = useMemo(() => ['Barns', ...titleSegments], [titleSegments]);
  const canViewBarnInfo = can('org', organizationId, Privilege.BARN_VIEW);
  const canAddBarns = can('org', organizationId, Privilege.ORG_BARN_CREATE);

  // --- Apollo/GraphQL hooks ---
  const { loading, error, data, refetch } = useQuery(ORG_BARNS_QUERY, {
    variables: {
      id: organizationId,
    },
    context: asUserContext,
    skip: !organizationId || !canViewBarnInfo,
  });

  const barns = data?.organization_by_pk?.barns;

  const barnsWithCounts = useMemo(() => {
    if (!barns) return [];

    return barns.map((barn) => ({
      ...barn,
      ...getBarnStructureCounts(barn),
    }));
  }, [barns]);

  const filteredAndSortedBarns = useMemo(() => {
    if (!barnsWithCounts) return [];

    let filtered = barnsWithCounts;

    if (searchTerm) {
      const fzf = new Fzf(barnsWithCounts, {
        selector: (item) => `${item.name}`,
        casing: 'case-insensitive',
      });
      filtered = fzf?.find(searchTerm)?.map((e) => e.item);
    }

    return _.orderBy(filtered, [sortField], [sortDirection]);
  }, [barnsWithCounts, searchTerm, sortField, sortDirection]);

  const simulatedBarnFilteredCount = filteredAndSortedBarns?.filter((barn) => barn.is_simulated)?.length || 0;
  const nonSimulatedBarnFilteredCount = filteredAndSortedBarns?.filter((barn) => !barn.is_simulated)?.length || 0;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (!canViewBarnInfo) {
    return (
      <Page className="OrgBarnsPage" titleSegments={['Barns', 'Access Denied']}>
        <div className="OrgBarnsTab-Content">
          <Card className="OrgBarnsTab-PermissionDenied" role="alert">
            <h2 className="OrgBarnsTab-PermissionDenied-Heading">Access Denied</h2>
            <p>You do not have permission to view the Barns tab for this organization.</p>
          </Card>
        </div>
      </Page>
    );
  }
  if (error) {
    return <span>Error {JSON.stringify(error)}</span>;
  }
  if (loading) {
    return <span>Loading...</span>;
  }

  return (
    <Page className="OrgBarnsPage" titleSegments={pageTitleSegments}>
      <div className="OrgBarnsTab-Content">
        <div className="OrgBarnsTab-barnListHeader">
          <h2>Barns</h2>
          <span>
            {filteredAndSortedBarns?.length} total ({simulatedBarnFilteredCount} simulated,{' '}
            {nonSimulatedBarnFilteredCount} physical)
          </span>
        </div>

        <div className="OrgBarnsTab-barnTableControls">
          <TextInput
            placeholder="Search Barns"
            value={searchTerm}
            label={''}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {!!organizationId && canAddBarns ? <CreateBarnForm orgId={organizationId} refetch={refetch} /> : <></>}
        </div>

        <div className="OrgViewer-AllBarns">
          {filteredAndSortedBarns?.length > 0 ? (
            <div className="OrgBarnsTab-barnTable">
              <div className="OrgBarnsTab-barnTableRow OrgBarnsTab-barnTableHeader ">
                {STRUCTURE_COLUMNS.map(({ field, label }) => (
                  <div
                    key={field}
                    className={`OrgBarnsTab-barnTableCell OrgBarnsTab-barnTableSortableHeader${
                      field === 'name' ? '' : ' OrgBarnsTab-barnTableCellCount'
                    }`}
                    onClick={() => handleSort(field)}
                  >
                    {label}
                    {sortField === field && (
                      <span className="OrgBarnsTab-barnTableSortableHeaderSortIndicator">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div className="OrgBarnsTab-barnTableBody">
                {filteredAndSortedBarns.map((barn) => (
                  <div className="OrgBarnsTab-barnTableRow" key={barn.id}>
                    <div className="OrgBarnsTab-barnTableCell">
                      <Link to={`/b/${barn.id}/settings`} className="OrgBarnsTab-link">
                        {censor(barn.name, 'barn')}
                        {barn.is_simulated ? <span className="OrgBarnsTab-simulatedText"> (simulated 🤖)</span> : ''}
                      </Link>
                    </div>
                    <div className="OrgBarnsTab-barnTableCell OrgBarnsTab-barnTableCellCount">{barn.feedLineCount}</div>
                    <div className="OrgBarnsTab-barnTableCell OrgBarnsTab-barnTableCellCount">{barn.roomCount}</div>
                    <div className="OrgBarnsTab-barnTableCell OrgBarnsTab-barnTableCellCount">{barn.binCount}</div>
                    <div className="OrgBarnsTab-barnTableCell OrgBarnsTab-barnTableCellCount">{barn.deviceCount}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="OrgBarnsTab-noResults">
              {searchTerm ? `No barns found matching "${searchTerm}"` : 'None'}
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

OrgBarnsTab.propTypes = {
  titleSegments: PropTypes.arrayOf(PropTypes.string),
  organizationId: PropTypes.string,
};

export default OrgBarnsTab;
